import { cn } from '../../lib/cn';

/** Page shell — fixed ambient gradients via ::before */
export const puRoot = cn(
  'relative isolate min-h-screen bg-[var(--cream-0)] font-[var(--font-body)] text-base leading-[1.55] tracking-[-0.005em] text-[var(--ink-1)] antialiased',
  'before:pointer-events-none before:fixed before:inset-0 before:z-0 before:opacity-70 before:content-[""]',
  'before:bg-[radial-gradient(820px_440px_at_16%_-12%,var(--gold-tint),transparent_62%),radial-gradient(1000px_540px_at_98%_-16%,var(--ai-tint),transparent_60%)]',
);

export const puContainer = 'relative z-10 mx-auto max-w-[580px] px-[18px] pb-[60px] pt-6 md:px-6 md:pb-20 md:pt-9';

export const puMono = 'font-[var(--font-mono)] tabular-nums';
export const puDisplay = 'font-[var(--font-display)] tracking-[-0.03em]';
export const puEyebrow =
  'font-[var(--font-mono)] text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[var(--ink-3)]';

export const puCard =
  'overflow-hidden rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]';

export const puBtnPrimary = cn(
  'inline-flex h-[52px] w-full cursor-pointer items-center justify-center gap-2 rounded-[var(--r-md)] border-0 px-[22px]',
  'font-[var(--font-body)] text-[15.5px] font-semibold tracking-[-0.005em] text-white',
  'bg-[var(--success)] shadow-[0_8px_18px_-10px_color-mix(in_srgb,var(--success)_70%,transparent)]',
  'transition-[background,transform] duration-150',
  'hover:bg-[color-mix(in_srgb,var(--success)_86%,#000)] active:translate-y-px',
  'disabled:cursor-default disabled:opacity-50 disabled:shadow-none',
);

export const puBtnSecondary = cn(
  'inline-flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-[var(--r-md)]',
  'border border-[var(--cream-2)] bg-[var(--cream-1)] px-5 font-[var(--font-body)] text-[14.5px] font-medium tracking-[-0.005em] text-[var(--ink-1)] shadow-[var(--sh-1)]',
  'transition-[background,border-color] duration-150 hover:border-[var(--cream-shadow)] hover:bg-[var(--cream-0)]',
);

export const puInput = cn(
  'pu-date-input h-[52px] w-full rounded-[var(--r-md)] border border-[var(--cream-shadow)] bg-[var(--cream-1)] px-4',
  'font-[var(--font-body)] text-[17px] tracking-[-0.005em] text-[var(--ink-1)] shadow-[var(--sh-1)] outline-none',
  'transition-[border-color,box-shadow] duration-150',
  'focus:border-[var(--success)] focus:shadow-[0_0_0_3px_var(--success-tint)]',
);

export const puTreatmentCard =
  'transition-[box-shadow,border-color] duration-[220ms] hover:shadow-[var(--sh-2)]';

export const puSpectrumBar = 'h-1 bg-[var(--grad-spectrum)]';

const PU_RISE_DELAYS = ['0ms', '80ms', '160ms', '240ms', '320ms', '400ms', '480ms', '560ms'] as const;

/** Staggered entrance — keyframe `pu-rise` lives in patient-urgency.css */
export function puRise(delayIndex: number): string {
  const delay = PU_RISE_DELAYS[Math.min(delayIndex, PU_RISE_DELAYS.length - 1)]!;
  return cn(
    'motion-reduce:animate-none',
    'animate-[pu-rise_520ms_var(--ease-out)_both]',
    `[animation-delay:${delay}]`,
  );
}
